import { type PseudoStateDataMap, type RuntimePseudoEvent, type RuntimePseudoState } from '../../types';
import { resolvePseudoStateData } from '../resolve-pseudo-state-data';

const getForcedStateData = <TStateData>(
  stateDataMap: PseudoStateDataMap<TStateData>,
  forcedState: RuntimePseudoState,
): TStateData | null => {
  switch (forcedState) {
    case 'default':
      return stateDataMap.defaultStateData;
    case 'hover':
      return stateDataMap.hoverStateData ?? null;
    case 'focus':
      return stateDataMap.focusStateData ?? null;
    case 'checked':
      return stateDataMap.checkedStateData ?? null;
    case 'active':
      return stateDataMap.activeStateData ?? null;
    default:
      return null;
  }
};

/**
 * Creates callback that switches data to matching pseudo state.
 *
 * @since ??
 *
 * @param {object} params Params.
 * @param {PseudoStateDataMap<any>} params.stateDataMap State data map.
 * @param {CallableFunction} params.setStateData Callback to apply resolved state data.
 *
 * @returns {CallableFunction} Pseudo-state switch handler.
 */
export const createSwitchDataToCurrentPseudoState = <TStateData>({
  stateDataMap,
  setStateData,
}: {
  stateDataMap: PseudoStateDataMap<TStateData>;
  setStateData: (stateData: TStateData, state: RuntimePseudoState, event?: RuntimePseudoEvent) => void;
}): ((hoverElement: HTMLElement, event?: RuntimePseudoEvent, forcedState?: RuntimePseudoState) => void) => {
  const switchDataToCurrentPseudoState = (
    hoverElement: HTMLElement,
    event?: RuntimePseudoEvent,
    forcedState?: RuntimePseudoState,
  ) => {
    if (forcedState) {
      const forcedStateData = getForcedStateData(stateDataMap, forcedState);

      if (null != forcedStateData) {
        setStateData(forcedStateData, forcedState, event);
        return;
      }
    }

    const { state, stateData } = resolvePseudoStateData({
      hoverElement,
      stateDataMap,
    });

    setStateData(stateData, state, event);
  };

  return switchDataToCurrentPseudoState;
};
